import React, { useState } from 'react';
import { settingsService } from '../services/settings';
import {
  RocketIcon,
  BarChartIcon,
  TargetIcon,
  TrendingUpIcon,
  DollarSignIcon,
  ZapIcon,
  RefreshCwIcon,
} from './Icons';
import './Onboarding.css';

interface OnboardingProps {
  onComplete: () => void;
  onSkip: () => void;
}

const Onboarding: React.FC<OnboardingProps> = ({ onComplete, onSkip }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [goal, setGoal] = useState('');

  const steps = [
    {
      title: 'Welcome to Infinity Rockets!',
      content: (
        <div className="onboarding-step-content">
          <div className="onboarding-icon"><RocketIcon size={48} /></div>
          <h2>Build productive habits that actually stick</h2>
          <p>
            Infinity Rockets helps you track your daily routines, visualize your progress, 
            and maintain consistency with a beautiful, privacy-first system.
          </p>
          <div className="onboarding-features">
            <div className="feature-item">
              <span className="feature-icon"><BarChartIcon size={20} /></span>
              <span>Visual Progress Tracking</span>
            </div>
            <div className="feature-item">
              <span className="feature-icon"><TargetIcon size={20} /></span>
              <span>Smart Routine Builder</span>
            </div>
            <div className="feature-item">
              <span className="feature-icon"><TrendingUpIcon size={20} /></span>
              <span>Deep Analytics</span>
            </div>
            <div className="feature-item">
              <span className="feature-icon"><DollarSignIcon size={20} /></span>
              <span>Budget Management</span>
            </div>
          </div>
        </div>
      ),
    },
    {
      title: 'Quick Setup',
      content: (
        <div className="onboarding-step-content">
          <div className="onboarding-icon"><ZapIcon size={48} /></div>
          <h2>Get started in seconds</h2>
          <p>You can start using Infinity Rockets right away! Here are some quick actions:</p>
          <div className="onboarding-actions">
            <button 
              className="onboarding-action-btn"
              onClick={() => {
                // Navigate to routines - will be handled by parent
                onComplete();
              }}
            >
              <span className="action-icon"><RefreshCwIcon size={20} /></span>
              <div>
                <strong>Create Your First Routine</strong>
                <p>Set up morning, work, or evening routines</p>
              </div>
            </button>
            <button 
              className="onboarding-action-btn"
              onClick={() => {
                // Navigate to tasks - will be handled by parent
                onComplete();
              }}
            >
              <span className="action-icon">✓</span>
              <div>
                <strong>Add Your First Task</strong>
                <p>Start tracking your to-dos</p>
              </div>
            </button>
            <button 
              className="onboarding-action-btn"
              onClick={() => {
                // Navigate to budget - will be handled by parent
                onComplete();
              }}
            >
              <span className="action-icon"><DollarSignIcon size={20} /></span>
              <div>
                <strong>Set Up Budget OS</strong>
                <p>Track your income and expenses</p>
              </div>
            </button>
          </div>
          <p className="onboarding-hint">You can skip this for now and explore on your own!</p>
        </div>
      ),
    },
    {
      title: 'Feature Tour',
      content: (
        <div className="onboarding-step-content">
          <div className="onboarding-icon">🎓</div>
          <h2>Discover Key Features</h2>
          <div className="feature-tour-list">
            <div className="tour-item">
              <span className="tour-icon"><BarChartIcon size={20} /></span>
              <div>
                <strong>Contribution Graph</strong>
                <p>See your daily activity at a glance with GitHub-style contribution graphs</p>
              </div>
            </div>
            <div className="tour-item">
              <span className="tour-icon"><RefreshCwIcon size={20} /></span>
              <div>
                <strong>Routines</strong>
                <p>Build custom routines with tasks that reset daily</p>
              </div>
            </div>
            <div className="tour-item">
              <span className="tour-icon"><DollarSignIcon size={20} /></span>
              <div>
                <strong>Budget OS</strong>
                <p>Track income, expenses, savings goals, and reserves</p>
              </div>
            </div>
            <div className="tour-item">
              <span className="tour-icon"><TrendingUpIcon size={20} /></span>
              <div>
                <strong>Analytics</strong>
                <p>Deep insights into your productivity patterns and financial health</p>
              </div>
            </div>
          </div>
        </div>
      ),
    },
    {
      title: 'Set Your Goal (Optional)',
      content: (
        <div className="onboarding-step-content">
          <div className="onboarding-icon"><TargetIcon size={48} /></div>
          <h2>What do you want to achieve?</h2>
          <p>Setting a goal helps you stay focused. You can change this anytime.</p>
          <div className="form-group">
            <textarea
              className="goal-input"
              value={goal}
              onChange={(e) => setGoal(e.target.value)}
              placeholder="e.g., Build a daily meditation habit, Save $10,000 this year, Complete 100 tasks this month..."
              rows={4}
            />
          </div>
          <p className="onboarding-hint">This is optional - you can skip this step!</p>
        </div>
      ),
    },
  ];

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleComplete = async () => {
    try {
      await settingsService.completeOnboarding();

      onComplete();
    } catch (error) {
      console.error('Error completing onboarding:', error);
      // Still call onComplete to close the modal
      onComplete();
    }
  };

  const handleSkip = async () => {
    try {
      await settingsService.completeOnboarding();
      onSkip();
    } catch (error) {
      console.error('Error skipping onboarding:', error);
      onSkip();
    }
  };

  return (
    <div className="onboarding-overlay">
      <div className="onboarding-modal">
        <div className="onboarding-header">
          <div className="onboarding-progress">
            {steps.map((_, index) => (
              <div
                key={index}
                className={`progress-dot ${index === currentStep ? 'active' : ''} ${index < currentStep ? 'completed' : ''}`}
              />
            ))}
          </div>
          <button className="onboarding-skip-btn" onClick={handleSkip}>
            Skip
          </button>
        </div>

        <div className="onboarding-content">
          <h1 className="onboarding-step-title">{steps[currentStep].title}</h1>
          {steps[currentStep].content}
        </div>

        <div className="onboarding-footer">
          {currentStep > 0 && (
            <button className="onboarding-btn onboarding-btn-secondary" onClick={handlePrevious}>
              Previous
            </button>
          )}
          <div className="onboarding-footer-spacer" />
          <button className="onboarding-btn onboarding-btn-primary" onClick={handleNext}>
            {currentStep === steps.length - 1 ? 'Get Started' : 'Next'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Onboarding;

